// 1.2.18: 用户资料服务 - 添加前端缓存优化
// 1.2.19: 扩展缓存机制，支持头像URL缓存
import { supabase, getStorageClient } from '../lib/supabase';
import { logger } from '../utils/logger';
import { getCurrentUser } from './authService';

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

// 1.2.18: 用户名缓存相关常量
// 1.2.19: 扩展为统一profile缓存（包含用户名和头像）
const USER_DISPLAY_NAME_CACHE_KEY_PREFIX = 'user_display_name_cache_';
const USER_PROFILE_CACHE_KEY_PREFIX = 'user_profile_cache_'; // 1.2.19: 新的统一缓存键前缀
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 1个月，30天
const CACHE_VERSION = '1.2.19'; // 1.2.19: 更新缓存版本，用于版本兼容性

// 1.2.18: 缓存数据结构
// 1.2.19: 扩展为ProfileCache，同时缓存用户名和头像URL
interface DisplayNameCache {
  userId: string;
  displayName: string | null;
  timestamp: number;
  version: string;
}

// 1.2.19: 新的统一profile缓存数据结构
interface ProfileCache {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null; // 1.2.19: 新增头像URL缓存
  timestamp: number;
  version: string;
}

// ==================== Display Name Cache Management (localStorage) ====================
// 1.2.19: 扩展为统一Profile缓存管理（包含用户名和头像URL）

/**
 * 1.2.18: 获取缓存键名
 * 1.2.19: 保留旧函数以保持向后兼容
 */
function getDisplayNameCacheKey(userId: string): string {
  return `${USER_DISPLAY_NAME_CACHE_KEY_PREFIX}${userId}`;
}

/**
 * 1.2.19: 获取统一profile缓存键名
 */
function getProfileCacheKey(userId: string): string {
  return `${USER_PROFILE_CACHE_KEY_PREFIX}${userId}`;
}

/**
 * 1.2.19: 获取统一profile缓存（包含用户名和头像URL）
 */
function getProfileCache(userId: string): ProfileCache | null {
  try {
    const cacheKey = getProfileCacheKey(userId);
    const data = localStorage.getItem(cacheKey);
    if (!data) {
      return null;
    }
    return JSON.parse(data) as ProfileCache;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error getting profile cache:', error);
    }
    // 缓存解析失败，清除无效缓存
    clearProfileCache(userId);
    return null;
  }
}

/**
 * 1.2.18: 获取用户名缓存
 * 1.2.19: 保留旧函数以保持向后兼容，内部调用新的getProfileCache
 */
function getDisplayNameCache(userId: string): DisplayNameCache | null {
  // 1.2.19: 尝试从新的统一缓存获取
  const profileCache = getProfileCache(userId);
  if (profileCache) {
    // 转换为旧格式以保持兼容
    return {
      userId: profileCache.userId,
      displayName: profileCache.displayName,
      timestamp: profileCache.timestamp,
      version: profileCache.version,
    };
  }
  
  // 1.2.19: 如果没有新缓存，尝试从旧缓存获取（迁移兼容）
  try {
    const cacheKey = getDisplayNameCacheKey(userId);
    const data = localStorage.getItem(cacheKey);
    if (!data) {
      return null;
    }
    return JSON.parse(data) as DisplayNameCache;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error getting display name cache:', error);
    }
    // 缓存解析失败，清除无效缓存
    clearDisplayNameCache(userId);
    return null;
  }
}

/**
 * 1.2.19: 设置统一profile缓存（包含用户名和头像URL）
 */
function setProfileCache(userId: string, displayName: string | null, avatarUrl: string | null): void {
  try {
    const cacheKey = getProfileCacheKey(userId);
    const cache: ProfileCache = {
      userId,
      displayName,
      avatarUrl, // 1.2.19: 新增头像URL缓存
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    if (import.meta.env.DEV) {
      logger.log(`✅ Cached profile for user ${userId}: displayName=${displayName || '(null)'}, avatarUrl=${avatarUrl ? 'exists' : '(null)'}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error setting profile cache:', error);
    }
  }
}

/**
 * 1.2.18: 设置用户名缓存
 * 1.2.19: 保留旧函数以保持向后兼容，内部调用新的setProfileCache
 */
function setDisplayNameCache(userId: string, displayName: string | null): void {
  // 1.2.19: 尝试从现有缓存获取头像URL，保持头像缓存不丢失
  const existingCache = getProfileCache(userId);
  const avatarUrl = existingCache?.avatarUrl || null;
  
  // 1.2.19: 使用新的统一缓存
  setProfileCache(userId, displayName, avatarUrl);
  
  // 1.2.19: 同时更新旧缓存以保持兼容（可选，逐步迁移）
  try {
    const cacheKey = getDisplayNameCacheKey(userId);
    const cache: DisplayNameCache = {
      userId,
      displayName,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    if (import.meta.env.DEV) {
      logger.log(`✅ Cached display name for user ${userId}: ${displayName || '(null)'}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error setting display name cache:', error);
    }
  }
}

/**
 * 1.2.19: 清除统一profile缓存
 */
export function clearProfileCache(userId: string): void {
  try {
    const cacheKey = getProfileCacheKey(userId);
    localStorage.removeItem(cacheKey);
    if (import.meta.env.DEV) {
      logger.log(`✅ Cleared profile cache for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error clearing profile cache:', error);
    }
  }
}

/**
 * 1.2.18: 清除用户名缓存
 * 1.2.19: 保留旧函数以保持向后兼容，同时清除新旧缓存
 */
export function clearDisplayNameCache(userId: string): void {
  // 1.2.19: 清除新的统一缓存
  clearProfileCache(userId);
  
  // 1.2.19: 清除旧缓存以保持兼容
  try {
    const cacheKey = getDisplayNameCacheKey(userId);
    localStorage.removeItem(cacheKey);
    if (import.meta.env.DEV) {
      logger.log(`✅ Cleared display name cache for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error clearing display name cache:', error);
    }
  }
}

/**
 * 1.2.19: 验证统一profile缓存有效性
 */
function isProfileCacheValid(cache: ProfileCache | null, userId: string): boolean {
  if (!cache) {
    return false;
  }
  
  // 检查用户ID是否匹配
  if (cache.userId !== userId) {
    return false;
  }
  
  // 检查版本是否匹配（版本不匹配时清除旧缓存）
  if (cache.version !== CACHE_VERSION) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Profile cache version mismatch (${cache.version} vs ${CACHE_VERSION}), clearing cache`);
    }
    clearProfileCache(userId);
    return false;
  }
  
  // 检查缓存是否过期
  const now = Date.now();
  const age = now - cache.timestamp;
  if (age > CACHE_TTL) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Profile cache expired (age: ${Math.round(age / 1000 / 60)} minutes), clearing cache`);
    }
    clearProfileCache(userId);
    return false;
  }
  
  return true;
}

/**
 * 1.2.18: 验证用户名缓存有效性
 * 1.2.19: 保留旧函数以保持向后兼容，优先检查新缓存
 */
function isDisplayNameCacheValid(cache: DisplayNameCache | null, userId: string): boolean {
  // 1.2.19: 优先检查新的统一缓存
  const profileCache = getProfileCache(userId);
  if (profileCache && isProfileCacheValid(profileCache, userId)) {
    return true;
  }
  
  // 1.2.19: 如果没有新缓存，检查旧缓存
  if (!cache) {
    return false;
  }
  
  // 检查用户ID是否匹配
  if (cache.userId !== userId) {
    return false;
  }
  
  // 检查版本是否匹配（版本不匹配时清除旧缓存）
  if (cache.version !== CACHE_VERSION) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Display name cache version mismatch (${cache.version} vs ${CACHE_VERSION}), clearing cache`);
    }
    clearDisplayNameCache(userId);
    return false;
  }
  
  // 检查缓存是否过期
  const now = Date.now();
  const age = now - cache.timestamp;
  if (age > CACHE_TTL) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Display name cache expired (age: ${Math.round(age / 1000 / 60)} minutes), clearing cache`);
    }
    clearDisplayNameCache(userId);
    return false;
  }
  
  return true;
}

/**
 * Get user profile
 * 1.2.11: 改进错误处理，解决 406 错误
 * - 添加认证状态验证
 * - 使用 .maybeSingle() 替代 .single() 避免 RLS 阻止时返回 406
 */
export async function getUserProfile(userId: string): Promise<{ profile: UserProfile | null; error: string | null }> {
  try {
    // 1.2.11: 先检查认证状态
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      logger.error('❌ No valid session for profile query:', {
        userId,
        sessionError: sessionError?.message
      });
      return { profile: null, error: '用户未认证，请重新登录' };
    }

    // 1.2.11: 验证请求的 userId 与当前认证用户匹配
    if (session.user.id !== userId) {
      logger.error('❌ User ID mismatch:', {
        requestedUserId: userId,
        authenticatedUserId: session.user.id
      });
      return { profile: null, error: '无权访问该用户资料' };
    }

    // 1.2.11: 使用 .maybeSingle() 替代 .single()，避免 RLS 阻止时返回 406
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle(); // 使用 maybeSingle() 而不是 single()

    if (error) {
      // 1.2.11: 处理 profile 不存在的情况
      if (error.code === 'PGRST116') {
        logger.log('ℹ️ Profile not found for user:', userId);
        return { profile: null, error: null };
      }
      
      // 1.2.11: 处理 406 错误
      if (error.message?.includes('406') || error.code === '406') {
        logger.error('❌ 406 Not Acceptable - RLS 策略可能阻止了访问:', {
          code: error.code,
          message: error.message,
          userId,
          hasSession: !!session,
          sessionUserId: session?.user?.id
        });
        return { profile: null, error: '访问被拒绝，请检查认证状态' };
      }
      
      logger.error('❌ Error getting user profile:', {
        code: error.code,
        message: error.message,
        userId
      });
      return { profile: null, error: error.message || '获取用户资料失败' };
    }

    return { profile: data as UserProfile, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while getting user profile:', {
      error: error.message,
      stack: error.stack,
      userId
    });
    return { profile: null, error: error.message || '获取用户资料失败' };
  }
}

/**
 * Create or update user profile
 */
async function upsertUserProfile(
  userId: string,
  updates: { display_name?: string | null; avatar_url?: string | null }
): Promise<{ profile: UserProfile | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: userId,
        ...updates,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'id'
      })
      .select()
      .single();

    if (error) {
      logger.error('❌ Error upserting user profile:', {
        code: error.code,
        message: error.message,
        userId,
        updates
      });
      return { profile: null, error: error.message || '更新用户资料失败' };
    }

    logger.log('✅ User profile upserted successfully:', {
      userId,
      updates
    });

    return { profile: data as UserProfile, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while upserting user profile:', {
      error: error.message,
      stack: error.stack,
      userId,
      updates
    });
    return { profile: null, error: error.message || '更新用户资料失败' };
  }
}

/**
 * Update user display name
 * 1.2.18: 更新后立即更新缓存
 * 1.2.19: 扩展缓存更新，保持头像URL缓存不丢失
 */
export async function updateDisplayName(
  userId: string,
  displayName: string
): Promise<{ profile: UserProfile | null; error: string | null }> {
  const result = await upsertUserProfile(userId, { display_name: displayName });
  
  // 1.2.19: 更新成功后立即更新统一缓存（保持头像URL缓存不丢失）
  if (result.profile) {
    setProfileCache(userId, result.profile.display_name, result.profile.avatar_url);
    // 1.2.19: 同时更新旧缓存以保持兼容
    setDisplayNameCache(userId, result.profile.display_name);
    if (import.meta.env.DEV) {
      logger.log('✅ Updated profile cache after display name update');
    }
  }
  
  return result;
}

/**
 * Upload avatar to Supabase Storage
 */
export async function uploadAvatar(
  userId: string,
  file: File
): Promise<{ url: string | null; error: string | null }> {
  try {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return { url: null, error: '不支持的图片格式，请使用 JPEG、PNG、WebP 或 GIF' };
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { url: null, error: '图片大小不能超过 5MB' };
    }

    // Generate file path: {userId}/{timestamp}.{ext}
    const timestamp = Date.now();
    const fileExt = file.name.split('.').pop() || 'jpg';
    const fileName = `${userId}/${timestamp}.${fileExt}`;

    logger.log('📤 Uploading avatar:', {
      userId,
      fileName,
      fileSize: `${(file.size / 1024).toFixed(2)}KB`,
      fileType: file.type
    });

    // 1.2.10: 使用 getStorageClient() 获取 Storage client（优先远程）
    const storageClient = getStorageClient();

    // Upload to Supabase Storage
    const { data, error } = await storageClient.storage
      .from('avatars')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      // 1.2.9: 改进错误处理，提供更友好的错误消息
      let errorMessage = error.message || '上传头像失败';
      
      // 检测常见的 Storage 错误
      if (error.message?.includes('name resolution failed') || 
          error.message?.includes('503') ||
          error.message?.includes('Service Temporarily Unavailable')) {
        errorMessage = '存储服务不可用。如果是本地开发环境，请使用远程 Supabase 项目的 Storage，或等待 Supabase 官方修复本地 Storage 问题。';
      } else if (error.message?.includes('Bucket not found') || 
                 error.message?.includes('does not exist')) {
        errorMessage = 'avatars 存储桶不存在。请先在 Supabase Dashboard 中创建 avatars bucket。';
      } else if (error.message?.includes('new row violates row-level security')) {
        errorMessage = '权限不足。请检查存储策略配置。';
      }
      
      logger.error('❌ Error uploading avatar:', {
        code: error.message,
        userId,
        fileName,
        errorMessage
      });
      return { url: null, error: errorMessage };
    }

    // Get public URL
    const { data: urlData } = storageClient.storage
      .from('avatars')
      .getPublicUrl(fileName);

    const publicUrl = urlData.publicUrl;

    logger.log('✅ Avatar uploaded successfully:', {
      userId,
      fileName,
      publicUrl
    });

    // Update profile with avatar URL
    const { error: updateError } = await upsertUserProfile(userId, { avatar_url: publicUrl });
    if (updateError) {
      if (import.meta.env.DEV) {
        logger.error('⚠️ Avatar uploaded but failed to update profile:', updateError);
      }
      // Still return the URL even if profile update failed
    } else {
      // 1.2.19: 上传成功后更新头像URL缓存
      const existingCache = getProfileCache(userId);
      const displayName = existingCache?.displayName || null;
      setProfileCache(userId, displayName, publicUrl);
      if (import.meta.env.DEV) {
        logger.log('✅ Updated avatar URL cache after upload');
      }
    }

    return { url: publicUrl, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while uploading avatar:', {
      error: error.message,
      stack: error.stack,
      userId
    });
    return { url: null, error: error.message || '上传头像失败' };
  }
}

/**
 * Delete old avatar from Storage
 */
export async function deleteAvatar(avatarUrl: string | null): Promise<{ success: boolean; error: string | null }> {
  if (!avatarUrl) {
    return { success: true, error: null };
  }

  try {
    // Extract file path from URL
    // URL format: https://{project}.supabase.co/storage/v1/object/public/avatars/{userId}/{timestamp}.{ext}
    const urlParts = avatarUrl.split('/avatars/');
    if (urlParts.length !== 2) {
      logger.warn('⚠️ Invalid avatar URL format:', avatarUrl);
      return { success: false, error: '无效的头像URL格式' };
    }

    const filePath = urlParts[1];
    
    logger.log('🗑️ Deleting old avatar:', { filePath });

    // 1.2.10: 使用 getStorageClient() 获取 Storage client（优先远程）
    const storageClient = getStorageClient();

    const { error } = await storageClient.storage
      .from('avatars')
      .remove([filePath]);

    if (error) {
      logger.error('❌ Error deleting avatar:', {
        code: error.message,
        filePath
      });
      // Don't fail the operation if deletion fails
      return { success: false, error: error.message };
    }

    logger.log('✅ Avatar deleted successfully:', { filePath });
    return { success: true, error: null };
  } catch (error: any) {
    logger.error('❌ Exception while deleting avatar:', {
      error: error.message,
      stack: error.stack,
      avatarUrl
    });
    return { success: false, error: error.message || '删除头像失败' };
  }
}

/**
 * Get current user's profile
 * 1.2.18: 优化 - 先返回缓存数据，后台同步最新数据
 * 1.2.19: 扩展缓存，同时返回用户名和头像URL
 */
export async function getCurrentUserProfile(): Promise<{ profile: UserProfile | null; error: string | null }> {
  const user = await getCurrentUser();
  if (!user) {
    return { profile: null, error: '用户未登录' };
  }

  // 1.2.19: 优先检查新的统一profile缓存
  const profileCache = getProfileCache(user.id);
  if (isProfileCacheValid(profileCache, user.id)) {
    const cachedDisplayName = profileCache!.displayName;
    const cachedAvatarUrl = profileCache!.avatarUrl;
    
    if (import.meta.env.DEV) {
      logger.log(`📦 Using cached profile for user ${user.id}: displayName=${cachedDisplayName || '(null)'}, avatarUrl=${cachedAvatarUrl ? 'exists' : '(null)'}`);
    }
    
    // 1.2.19: 后台异步同步最新数据（不阻塞返回）
    syncProfileFromBackend(user.id).catch((error) => {
      if (import.meta.env.DEV) {
        logger.error('❌ Error syncing profile from backend:', error);
      }
      // 后台同步失败不影响已返回的缓存数据
    });
    
    // 1.2.19: 立即返回缓存数据（包含用户名和头像URL）
    return {
      profile: {
        id: user.id,
        display_name: cachedDisplayName,
        avatar_url: cachedAvatarUrl, // 1.2.19: 从缓存返回头像URL
        created_at: '',
        updated_at: '',
      },
      error: null,
    };
  }

  // 1.2.18: 兼容旧缓存（如果没有新缓存，检查旧缓存）
  const cache = getDisplayNameCache(user.id);
  let cachedDisplayName: string | null = null;
  
  if (isDisplayNameCacheValid(cache, user.id)) {
    cachedDisplayName = cache!.displayName;
    if (import.meta.env.DEV) {
      logger.log(`📦 Using cached display name for user ${user.id}: ${cachedDisplayName || '(null)'}`);
    }
    
    // 1.2.18: 后台异步同步最新数据（不阻塞返回）
    syncProfileFromBackend(user.id).catch((error) => {
      if (import.meta.env.DEV) {
        logger.error('❌ Error syncing profile from backend:', error);
      }
      // 后台同步失败不影响已返回的缓存数据
    });
    
    // 立即返回缓存数据（构造一个简化的 profile 对象）
    return {
      profile: {
        id: user.id,
        display_name: cachedDisplayName,
        avatar_url: null, // 1.2.18: 旧缓存不包含头像URL
        created_at: '',
        updated_at: '',
      },
      error: null,
    };
  }

  // 1.2.18: 缓存无效或不存在，从后端获取
  return await syncProfileFromBackend(user.id);
}

/**
 * 1.2.18: 从后端同步用户资料并更新缓存
 * 1.2.19: 扩展缓存，同时更新用户名和头像URL
 */
async function syncProfileFromBackend(userId: string): Promise<{ profile: UserProfile | null; error: string | null }> {
  const result = await getUserProfile(userId);
  
  // 1.2.19: 更新统一缓存（同时缓存 display_name 和 avatar_url）
  if (result.profile) {
    setProfileCache(userId, result.profile.display_name, result.profile.avatar_url);
    // 1.2.19: 同时更新旧缓存以保持兼容
    setDisplayNameCache(userId, result.profile.display_name);
    if (import.meta.env.DEV) {
      logger.log(`✅ Synced profile from backend for user ${userId}`);
    }
  } else if (result.error === null) {
    // Profile 不存在，缓存 null
    setProfileCache(userId, null, null);
    setDisplayNameCache(userId, null);
  }
  
  return result;
}

